import React, { useEffect, useMemo, useState } from "react";
import { Card, Tag, Typography } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBatteryHalf,
  faCodeBranch,
  faGlobe,
  faServer,
  faStore,
  faToolbox,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";

const { Text } = Typography;

/** ===================== Types locales (sin declare global) ===================== */

/** ===================== Helpers ===================== */
type NotificationPermissionState = "granted" | "denied" | "default" | "unsupported";

const boolTag = (v?: boolean | null) => (
  <Tag
    className="generalStyles__ellipsis__150px generalStyles__textCenter"
    color={v ? "green" : "red"}
  >
    {v ? "Yes" : "No"}
  </Tag>
);

function parseBrowser(ua: string): { name: string; version: string } {
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge;
  const getVersion = (re: RegExp) => ua.match(re)?.[1] ?? "unknown";

  if (isEdge) return { name: "Edge", version: getVersion(/Edg\/([\d.]+)/) };
  if (isChrome) return { name: "Chrome", version: getVersion(/Chrome\/([\d.]+)/) };
  if (isFirefox) return { name: "Firefox", version: getVersion(/Firefox\/([\d.]+)/) };
  if (isSafari) return { name: "Safari", version: getVersion(/Version\/([\d.]+)/) };
  return { name: "Unknown", version: "unknown" };
}

function getPlatform(): string {
  const nav: any = navigator;
  if (nav.userAgentData?.platform) return nav.userAgentData.platform;
  return navigator.platform || "unknown";
}

function testLocalStorage(): boolean {
  try {
    const k = "__ls_test__";
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function testSessionStorage(): boolean {
  try {
    const k = "__ss_test__";
    sessionStorage.setItem(k, "1");
    sessionStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function testWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
  } catch {
    return false;
  }
}

/** ===================== State types ===================== */
interface NetworkState {
  online: boolean;
  type: string | null;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean | null;
}

interface BatteryState {
  supported: boolean;
  level: number | null; // 0..1
  charging: boolean | null;
}

interface StorageState {
  supported: boolean;
  quota: number | null;
  usage: number | null;
}

interface CapsState {
  serviceWorker: boolean;
  notifications: NotificationPermissionState;
  clipboard: boolean;
  mediaDevices: boolean;
  localStorage: boolean;
  sessionStorage: boolean;
  webgl: boolean;
}

/** ===================== Componente ===================== */
const DashboardSystemDetails: React.FC = () => {
  const [network, setNetwork] = useState<NetworkState>({
    online: navigator.onLine,
    type: null,
    effectiveType: null,
    downlink: null,
    rtt: null,
    saveData: null,
  });

  const [battery, setBattery] = useState<BatteryState>({
    supported: false,
    level: null,
    charging: null,
  });

  const [storage, setStorage] = useState<StorageState>({
    supported: false,
    quota: null,
    usage: null,
  });

  const [caps] = useState<CapsState>({
    serviceWorker: "serviceWorker" in navigator,
    notifications: typeof Notification === "undefined" ? "unsupported" : Notification.permission,
    clipboard: !!navigator.clipboard,
    mediaDevices: !!navigator.mediaDevices,
    localStorage: testLocalStorage(),
    sessionStorage: testSessionStorage(),
    webgl: testWebGL(),
  });

  // Datos estáticos
  const browser = useMemo(() => parseBrowser(navigator.userAgent), []);
  const platform = useMemo(() => getPlatform(), []);
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const languages =
    (navigator.languages && navigator.languages.length > 0
      ? navigator.languages.join(", ")
      : navigator.language) || "unknown";
  const screenInfo = useMemo(
    () => ({
      width: window.screen?.width,
      height: window.screen?.height,
      dpr: window.devicePixelRatio || 1,
    }),
    [],
  );

  // Network Info API
  useEffect(() => {
    const nav: any = navigator;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

    const readConn = () => {
      setNetwork({
        online: navigator.onLine,
        type: conn?.type ?? null,
        effectiveType: conn?.effectiveType ?? null,
        downlink: conn?.downlink ?? null,
        rtt: conn?.rtt ?? null,
        saveData: conn?.saveData ?? null,
      });
    };

    readConn();

    const onOnline = () => setNetwork((p) => ({ ...p, online: true }));
    const onOffline = () => setNetwork((p) => ({ ...p, online: false }));
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    conn?.addEventListener?.("change", readConn);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      conn?.removeEventListener?.("change", readConn);
    };
  }, []);

  // Battery (si soporta)
  useEffect(() => {
    let mounted = true;
    const nav: any = navigator;
    if (nav.getBattery) {
      nav.getBattery().then((batt: any) => {
        if (!mounted) return;
        const update = () =>
          setBattery({
            supported: true,
            level: batt.level,
            charging: batt.charging,
          });
        update();
        batt.addEventListener("levelchange", update);
        batt.addEventListener("chargingchange", update);
      });
    }
    return () => {
      mounted = false;
    };
  }, []);

  // Storage estimate
  useEffect(() => {
    let mounted = true;
    if (navigator.storage?.estimate) {
      navigator.storage.estimate().then((est: any) => {
        if (!mounted) return;
        setStorage({
          supported: true,
          quota: est?.quota ?? null,
          usage: est?.usage ?? null,
        });
      });
    }
    return () => {
      mounted = false;
    };
  }, []);

  const ValuePill: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
    children = null,
    className = "",
  }) => (
    <Tag
      color="blue"
      className={`generalStyles__ellipsis__150px generalStyles__textCenter ${className ?? ""}`}
    >
      {children ?? "unknown"}
    </Tag>
  );

  const InfoRow: React.FC<React.PropsWithChildren<{ label: string; className?: string }>> = ({
    label,
    className = "",
    children = null,
  }) => (
    <div className={`generalStyles__flex ${className}`}>
      <div className="generalStyles__ellipsis__200px">{label}</div>
      {children}
    </div>
  );

  return (
    <div>
      <div className="generalStyles__dashboardCardContainer">
        {/* NETWORK */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon icon={faWifi} className="generalStyles__info generalStyles__mrFix" />
              <span>Network</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Online">{boolTag(network.online)}</InfoRow>

          <InfoRow label="Type" className="mt-2">
            <ValuePill>{network.type ?? "unknown"}</ValuePill>
          </InfoRow>

          <InfoRow label="Effective Type" className="mt-2">
            <ValuePill>{network.effectiveType ?? "unknown"}</ValuePill>
          </InfoRow>

          <InfoRow label="Downlink (Mbps)" className="mt-2">
            <ValuePill>{network.downlink ?? "unknown"}</ValuePill>
          </InfoRow>

          <InfoRow label="RTT (ms)" className="mt-2">
            <ValuePill>{network.rtt ?? "unknown"}</ValuePill>
          </InfoRow>

          <InfoRow label="Data saver" className="mt-2">
            <ValuePill>
              {network.saveData === null ? "unknown" : String(network.saveData)}
            </ValuePill>
          </InfoRow>
        </Card>

        {/* BROWSER */}
        <Card
          hoverable
          className="generalStyles__dashboardCardXL"
          title={
            <div>
              <FontAwesomeIcon
                icon={faGlobe}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Browser</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Name/Version">
            <ValuePill>{`${browser.name} ${browser.version}`}</ValuePill>
          </InfoRow>

          <InfoRow label="Languages" className="mt-2">
            <ValuePill>{languages}</ValuePill>
          </InfoRow>

          <InfoRow label="Cookies enabled" className="mt-2">
            {boolTag(navigator.cookieEnabled)}
          </InfoRow>

          <InfoRow label="User Agent" className="mt-2">
            <Text code style={{ marginLeft: "100px" }}>
              {navigator.userAgent}
            </Text>
          </InfoRow>
        </Card>

        {/* DEVICE */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon
                icon={faServer}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Device</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Platform / OS">
            <ValuePill>{platform}</ValuePill>
          </InfoRow>

          <InfoRow label="CPU cores" className="mt-2">
            <ValuePill>
              {typeof navigator.hardwareConcurrency === "number"
                ? navigator.hardwareConcurrency
                : "unknown"}
            </ValuePill>
          </InfoRow>

          <InfoRow label="Screen" className="mt-2">
            <ValuePill>
              {screenInfo.width}×{screenInfo.height} @ DPR {screenInfo.dpr}
            </ValuePill>
          </InfoRow>

          <InfoRow label="Timezone" className="mt-2">
            <ValuePill>{timezone}</ValuePill>
          </InfoRow>
        </Card>

        {/* CAPABILITIES */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon
                icon={faToolbox}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Device</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Service Worker">{boolTag(caps.serviceWorker)}</InfoRow>
          <InfoRow label="WebGL" className="mt-2">
            {boolTag(caps.webgl)}
          </InfoRow>
          <InfoRow label="Notifications" className="mt-2">
            <ValuePill>
              {caps.notifications === "unsupported" ? "unsupported" : caps.notifications}
            </ValuePill>
          </InfoRow>
          <InfoRow label="Clipboard" className="mt-2">
            {boolTag(caps.clipboard)}
          </InfoRow>
          <InfoRow label="Media Devices" className="mt-2">
            {boolTag(caps.mediaDevices)}
          </InfoRow>
          <InfoRow label="LocalStorage" className="mt-2">
            {boolTag(caps.localStorage)}
          </InfoRow>
          <InfoRow label="SessionStorage" className="mt-2">
            {boolTag(caps.sessionStorage)}
          </InfoRow>
        </Card>

        {/* BATTERY */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon
                icon={faBatteryHalf}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Device</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Supported">{boolTag(battery.supported)}</InfoRow>

          <InfoRow label="Level" className="mt-2">
            <ValuePill>
              {battery.level != null ? `${Math.round(battery.level * 100)}%` : "unknown"}
            </ValuePill>
          </InfoRow>

          <InfoRow label="Charging" className="mt-2">
            {battery.charging != null ? boolTag(battery.charging) : <ValuePill>unknown</ValuePill>}
          </InfoRow>
        </Card>

        {/* STORAGE */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon
                icon={faStore}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Device</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Supported">{boolTag(storage.supported)}</InfoRow>

          <InfoRow label="Usage / Quota" className="mt-2">
            {storage.usage != null && storage.quota != null ? (
              <ValuePill>
                {(storage.usage / 1024 ** 2).toFixed(1)} MB /{" "}
                {(storage.quota / 1024 ** 3).toFixed(1)} GB
              </ValuePill>
            ) : (
              <ValuePill>unknown</ValuePill>
            )}
          </InfoRow>
        </Card>

        {/* VERSION */}
        <Card
          hoverable
          className="generalStyles__dashboardCard"
          title={
            <div>
              <FontAwesomeIcon
                icon={faCodeBranch}
                className="generalStyles__info generalStyles__mrFix"
              />
              <span>Version</span>
            </div>
          }
          style={{ margin: "10px" }}
        >
          <InfoRow label="Version" className="mt-2">
            <ValuePill>V1.1.6</ValuePill>
          </InfoRow>

          <InfoRow label="Date" className="mt-2">
            <ValuePill>August 11 - 2025</ValuePill>
          </InfoRow>
        </Card>
      </div>
    </div>
  );
};

export default DashboardSystemDetails;
